import { Link } from 'react-router-dom'; 
import Layout from '../../components/feature/Layout'; 
import { groupCompanies, visibleAssociateCompanies } from '../../mocks/companies'; 
import { useState, useRef, useCallback, useEffect } from 'react'; 
import { useTranslation } from 'react-i18next'; 

const pastelColorMap: Record<string, string> = { 
  '#000000': '#A2D7DD',
　'#2D2D2D': '#A8B4C0', 
  '#5C4033': '#C4A882', 
  '#1A1A1A': '#9BA8A3', 
  '#1B3A4B': '#89B0C4', 
  '#E85D3A': '#F4A68C', 
  '#333333': '#B0AEBC', 
}; 

// ---- スクロール中央検出 ---- 
function useScrollCenter(items: { slug: string }[]) { 
  const [activeSlug, setActiveSlug] = useState<string | null>(items[0]?.slug ?? null); 
  const scrollRef = useRef<HTMLDivElement>(null); 
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null); 
  const detectCenter = useCallback(() => { 
    const container = scrollRef.current; 
    if (!container) return; 
    const containerCenter = container.scrollLeft + container.clientWidth / 2; 
    let closestSlug: string | null = null; 
    let closestDist = Infinity; 
    const cards = container.querySelectorAll<HTMLElement>('[data-slug]'); 
    cards.forEach((card) => { 
      const cardCenter = card.offsetLeft + card.offsetWidth / 2; 
      const dist = Math.abs(containerCenter - cardCenter); 
      if (dist < closestDist) { closestDist = dist; closestSlug = card.dataset.slug ?? null; } 
    }); 
    setActiveSlug(closestSlug); 
  }, []); 
  const handleScroll = useCallback(() => { 
    if (timerRef.current) clearTimeout(timerRef.current); 
    detectCenter(); 
    timerRef.current = setTimeout(() => { detectCenter(); }, 16); 
  }, [detectCenter]); 
  useEffect(() => { 
    const raf = requestAnimationFrame(() => { detectCenter(); }); 
    return () => cancelAnimationFrame(raf); 
  }, [detectCenter]); 
  return { activeSlug, scrollRef, handleScroll }; 
} 

function useSlideOnView(threshold = 0.25) { 
  const ref = useRef<HTMLDivElement>(null); 
  const [triggered, setTriggered] = useState(false); 
  useEffect(() => { 
    const el = ref.current; 
    if (!el) return; 
    const observer = new IntersectionObserver(([entry]) => { 
      if (entry.isIntersecting && !triggered) { setTriggered(true); } 
    }, { threshold }); 
    observer.observe(el); 
    return () => observer.disconnect(); 
  }, [triggered, threshold]); 
  return { ref, triggered }; 
} 

const trimPunctuation = (text: string) => { 
  if (!text) return ''; 
  return text.replace(/[。\.]$/, ''); 
}; 

// ---- 各セクション用共通ラベルコンポーネント ----
function SectionLabel({ children, colorClass = "text-gray-400" }: { children: React.ReactNode, colorClass?: string }) {
  return (
    <p 
      className={`text-[11px] tracking-[0.25em] font-medium uppercase mb-3 ${colorClass}`} 
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {children}
    </p>
  );
}

function HeroSection() { 
  const { i18n } = useTranslation(); 
  const isJa = i18n.language === 'ja'; 
  const [showScrollBtn, setShowScrollBtn] = useState(true); 
   
  useEffect(() => { 
    const handleScroll = () => { setShowScrollBtn(window.scrollY <= 40); }; 
    window.addEventListener('scroll', handleScroll, { passive: true }); 
    return () => window.removeEventListener('scroll', handleScroll); 
  }, []); 

  const customSmoothScroll = (targetY: number, duration: number) => { 
    const html = document.documentElement; 
    const originalStyle = html.style.scrollBehavior; 
    html.style.scrollBehavior = 'auto';  
    const startY = window.pageYOffset; 
    const HEADER_OFFSET = 80; 
    const finalTargetY = targetY - HEADER_OFFSET; 
    const difference = finalTargetY - startY; 
    let startTime: number | null = null; 
    const step = (currentTime: number) => { 
      if (!startTime) startTime = currentTime; 
      const progress = currentTime - startTime; 
      const percent = Math.min(progress / duration, 1); 
      const easeInOutCubic = percent < 0.5 ? 4 * percent * percent * percent : 1 - Math.pow(-2 * percent + 2, 3) / 2; 
      window.scrollTo(0, startY + difference * easeInOutCubic); 
      if (progress < duration) { window.requestAnimationFrame(step); } else { html.style.scrollBehavior = originalStyle; } 
    }; 
    window.requestAnimationFrame(step); 
  }; 

  return ( 
    <section className="relative hero-color-shift"> 
      <style>{` 
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap');
        @keyframes colorShift { 
          0%   { background-color: #f0f4f8; } 
          16%  { background-color: #f5f0f8; } 
          33%  { background-color: #f8f4ee; } 
          50%  { background-color: #eef5f0; } 
          66%  { background-color: #f0eef8; } 
          83%  { background-color: #f8eef2; } 
          100% { background-color: #f0f4f8; } 
        } 
        @keyframes heroReveal { 
          0% { opacity: 0; transform: translateY(15px); } 
          100% { opacity: 1; transform: translateY(0); } 
        } 
        @keyframes cardSlideIn { 
          0%   { transform: translateX(-80vw); opacity: 0; } 
          100% { transform: translateX(0px); opacity: 1; } 
        } 
        .hero-color-shift { animation: colorShift 20s ease-in-out infinite; } 
        .hero-reveal { animation: heroReveal 2.5s ease-out both; } 
        .reveal-delay { animation-delay: 0.6s; } 
        .card-slide-in { animation: cardSlideIn 1.2s cubic-bezier(0.22, 1, 0.36, 1) both; } 
      `}</style> 
       
      <div className={`absolute top-3 left-1/2 -translate-x-1/2 z-20 transition-all duration-700 ${showScrollBtn ? 'opacity-100' : 'opacity-0'}`}> 
        <button onClick={() => { const f = document.querySelector('footer'); if(f) customSmoothScroll(f.offsetTop, 2000); }} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-300/60 hover:bg-gray-300/90 text-gray-500 cursor-pointer"> 
          <i className="ri-arrow-down-s-line ri-lg"></i> 
        </button> 
      </div> 

      <div className="md:hidden px-6 pt-16 pb-16 flex flex-col items-center text-center min-h-[60vh] justify-end"> 
        <div className="mb-10 h-[28px] hero-reveal" aria-hidden="true" />
        <h1 className="text-[16px] leading-[1.6] uppercase tracking-tighter mb-6 font-bold hero-reveal" style={{ fontFamily: "'Josefin Sans', sans-serif", animationDelay: '0.3s' }}> 
          THEY SAY SKY'S THE LIMIT.<br />NOW THE SKY'S OUR POINT OF VIEW 
        </h1> 
        <div className="text-[11px] leading-[1.9] text-gray-500 mb-10 hero-reveal reveal-delay fade-target" style={isJa ? { fontFamily: "'Noto Sans JP', sans-serif", wordBreak: 'keep-all', overflowWrap: 'normal' } : {}}> 
          {isJa ? ( 
            <> 
              <span className="block h-[84px]" aria-hidden="true" />
              <span className="block mt-4 text-[13px] font-bold text-gray-800 tracking-wider" style={{ wordBreak: 'keep-all', overflowWrap: 'normal' }}>私たちは<br />異なる視点の力が未来を形作ると信じています</span> 
            </> 
          ) : ( 
            <> 
              <span className="block h-[42px]" aria-hidden="true" />
              <span className="block mt-4 text-[16px] font-bold text-gray-800">We believe that the power of diverse perspectives shapes the future.</span> 
            </> 
          )} 
        </div> 
        <button onClick={() => {  
          const c = document.getElementById('companies');  
          if(c) { 
            const rect = c.getBoundingClientRect(); 
            const absoluteTop = rect.top + window.pageYOffset; 
            customSmoothScroll(absoluteTop, 2000);  
          } 
        }} className="px-6 py-3 text-[9px] tracking-[0.2em] font-bold border border-gray-300 rounded-sm hover:bg-black hover:text-white cursor-pointer bg-transparent uppercase"> 
          OUR COMPANIES 
        </button> 
      </div> 

      <div className="hidden md:block pt-32 pb-24"> 
        <div className="site-container"> 
          <div className="max-w-4xl"> 
            <div className="mb-14 h-[28px] hero-reveal" aria-hidden="true" />
            <h1 className="text-[44px] leading-[1.3] uppercase tracking-tight font-bold hero-reveal" style={{ fontFamily: "'Josefin Sans', sans-serif", animationDelay: '0.3s' }}> 
              THEY SAY SKY'S THE LIMIT.<br />NOW THE SKY'S OUR POINT OF VIEW 
            </h1> 
            <div className="mt-8 text-[16px] leading-[1.9] text-gray-500 max-w-none hero-reveal reveal-delay fade-target" style={isJa ? { fontFamily: "'Noto Sans JP', sans-serif" } : {}}> 
              {isJa ? ( 
                <> 
                  <span className="block h-[61px]" aria-hidden="true" />
                  <span className="block mt-6 text-[22px] font-bold text-gray-800 tracking-wider">私たちは、異なる視点の力が未来を形作ると信じています</span> 
                </> 
              ) : ( 
                <> 
                  <span className="block h-[61px]" aria-hidden="true" />
                  <span className="block mt-6 text-[22px] font-bold text-gray-800">We believe that the power of diverse perspectives shapes the future.</span> 
                </> 
              )} 
            </div> 
            <div className="mt-10 flex gap-6 hero-reveal reveal-delay"> 
              <button onClick={() => {  
                const c = document.getElementById('companies');  
                if(c) { 
                  const rect = c.getBoundingClientRect(); 
                  const absoluteTop = rect.top + window.pageYOffset; 
                  customSmoothScroll(absoluteTop, 2000);  
                } 
              }} className="px-8 py-4 text-[11px] tracking-[0.2em] font-bold border border-gray-300 rounded-sm hover:bg-black hover:text-white hover:-translate-y-1 shadow-sm hover:shadow-lg cursor-pointer bg-transparent uppercase"> 
                OUR COMPANIES 
              </button> 
            </div> 
          </div> 
        </div> 
      </div> 
    </section> 
  ); 
} 

function PhilosophySection() { 
  const { i18n } = useTranslation(); 
  const isJa = i18n.language === 'ja'; 
  return ( 
    <section className="bg-[#FAFAF8] border-t border-gray-100 py-12 md:py-20"> 
      <div className="site-container grid grid-cols-1 lg:grid-cols-12 gap-10"> 
        <div className="lg:col-span-3"> 
          <SectionLabel colorClass="text-gray-600">PHILOSOPHY</SectionLabel>
        </div> 
        <div className="lg:col-span-9"> 
          <p className="text-[12px] lg:text-[20px] leading-[2.0] text-gray-800 tracking-wider fade-target" style={isJa ? { fontFamily: "'Noto Sans JP', sans-serif" } : {}}> 
            {isJa ? ( 
              <> 
                私たちは、異なる視点が並外れた価値を生み出すと信じています<br />
                多様なポートフォリオを通じて、マーケティング、クリエイティブ、ライフスタイル、ビジネスの交差点でイノベーションを育みます
              </> 
            ) : ( 
              <> 
                We believe that different perspectives create extraordinary value.<br /> 
                Through our diverse portfolio, we foster innovation at the intersection of<br className="hidden md:inline" /> 
                marketing, creative, lifestyle, and business. 
              </> 
            )} 
          </p> 
        </div> 
      </div> 
    </section> 
  ); 
} 

function CompanyCard({ company, index, activeSlug }: { company: any; index: number; activeSlug: string | null; }) { 
  const { i18n } = useTranslation(); 
  const isJa = i18n.language === 'ja'; 
  const [hovered, setHovered] = useState(false); 
  const isActive = hovered || activeSlug === company.slug; 
  const pastelBg = pastelColorMap[company.accentColor] || '#C8C8D0'; 
   
  const isHoldings = company.slug === 'holdings' || company.slug === 'swell-holdings'; 
  const isBfp = company.slug === 'bfp'; 
  const isPartners = company.slug === 'partners' || company.name.includes('PARTNERS');  
  const isMeet = company.slug === 'meet'; 
  const isRiceBall = company.slug === 'riceball' || company.slug === 'rice-ball'; 
  const isCoffee = company.slug.includes('coffee'); 
   
  const getLogoSrc = () => { 
    if (isPartners) return '/logo/sp.png';  
    if (isRiceBall) return '/logo/rb.png'; 
    if (isCoffee) return isActive ? company.logo : '/logo/sweel.b.png'; 
    if (isMeet || isBfp) return isActive ? `/logo/${company.slug}.png` : `/logo/${company.slug}.b.png`; 
    return company.logo; 
  }; 

  return ( 
    <Link to={`/company/${company.slug}`} className="group flex flex-col h-full rounded-lg transition-all duration-500 ease-in-out cursor-pointer overflow-hidden border border-gray-100" style={{ backgroundColor: isActive ? pastelBg : '#ffffff', borderColor: isActive ? pastelBg : '#e8e8e8', boxShadow: isActive ? '0 10px 30px rgba(0,0,0,0.1)' : 'none', }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} > 
      <div className="relative w-full h-44 flex flex-col items-start justify-start px-8 pt-12"> 
        <span className="absolute top-6 right-8 text-[11px] font-medium tracking-wider transition-colors duration-500" style={{ color: isActive ? 'rgba(255,255,255,0.6)' : '#d1d5db' }} > {String(index + 1).padStart(2, '0')} </span> 
       <img  
  src={isHoldings ? "/logo/sh.png" : getLogoSrc()}  
  alt={company.name}  
  className={`object-contain object-left transition-all duration-300 ${  
    (isHoldings || isPartners) ? 'max-w-[200px] max-h-[150px]' : // ← SHとPartnersを共通化
    isBfp ? 'max-w-[185px] max-h-[140px]' :  
    'max-w-[125px] max-h-[100px]'  
  }`}  
/>
      </div> 
      <div className="px-8 pb-8 flex flex-col flex-grow"> 
        <h3 className="text-[16px] font-semibold tracking-[0.02em] mb-2 transition-colors duration-500" style={{ fontFamily: "'Inter', sans-serif", color: isActive ? '#ffffff' : '#111827' }} > {company.name} </h3> 
        <p className="text-[13px] mb-4 transition-colors duration-500" style={{ color: isActive ? 'rgba(255,255,255,0.75)' : '#9ca3af' }}> {company.tagline} </p> 
        <p className="text-[13px] leading-[1.7] line-clamp-2 transition-colors duration-500 fade-target" style={Object.assign({ color: isActive ? 'rgba(255,255,255,0.8)' : '#6b7280' }, isJa ? { fontFamily: "'Noto Sans JP', sans-serif" } : {})}> 
          {trimPunctuation(isJa ? (company.description_ja || company.description) : company.description)} 
        </p> 
        <div className="mt-auto pt-6 flex items-center gap-2"> 
          <span className="text-[12px] tracking-[0.05em] font-medium transition-colors duration-500" style={{ color: isActive ? '#ffffff' : '#9ca3af' }}> VIEW DETAILS </span> 
          <i className={`ri-arrow-right-line text-[14px] transition-all duration-500 ${isActive ? 'translate-x-1 text-white' : 'text-gray-400'}`} /> 
        </div> 
      </div> 
    </Link> 
  ); 
} 

function CompaniesSection() { 
  const { activeSlug, scrollRef, handleScroll } = useScrollCenter(groupCompanies); 
  const { ref: slideRef, triggered } = useSlideOnView(0.25); 
  return ( 
    <section id="companies" className="bg-white py-14 md:py-24"> 
      <div className="site-container"> 
        <div className="mb-10 md:mb-14"> 
          <SectionLabel>GROUP COMPANIES</SectionLabel> 
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>Our Portfolio</h2> 
        </div> 
        <div ref={slideRef} className="md:hidden"> 
          <div ref={scrollRef} className="-mx-6 overflow-x-auto scrollbar-hide flex gap-4 pb-4 px-6 snap-x snap-mandatory" onScroll={handleScroll}> 
            {groupCompanies.map((company, i) => ( 
              <div key={company.slug} data-slug={company.slug} className={`w-[78vw] shrink-0 snap-center ${triggered ? 'card-slide-in' : ''}`} style={{ animationDelay: `${i * 0.1}s`, opacity: triggered ? 1 : 0 }}> 
                <CompanyCard company={company} index={i} activeSlug={activeSlug} /> 
              </div> 
            ))} 
          </div> 
        </div> 
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6"> 
          {groupCompanies.map((company, i) => ( <CompanyCard key={company.slug} company={company} index={i} activeSlug={null} /> ))} 
        </div> 
      </div> 
    </section> 
  ); 
} 

function AssociatesSection() { 
  const { activeSlug, scrollRef, handleScroll } = useScrollCenter(visibleAssociateCompanies); 
  const { ref: slideRef, triggered } = useSlideOnView(0.25); 
  return ( 
    <section className="bg-[#FAFAF8] border-t border-gray-100 py-14 md:py-24"> 
      <div className="site-container"> 
        <div className="mb-10 md:mb-14"> 
          <SectionLabel>ASSOCIATE COMPANIES</SectionLabel> 
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>Our Associates</h2> 
        </div> 
        <div ref={slideRef} className="md:hidden"> 
          <div ref={scrollRef} className="-mx-6 overflow-x-auto scrollbar-hide flex gap-4 pb-4 px-6 snap-x snap-mandatory" onScroll={handleScroll}> 
            {visibleAssociateCompanies.map((company, i) => ( 
              <div key={company.slug} data-slug={company.slug} className={`w-[78vw] shrink-0 snap-center ${triggered ? 'card-slide-in' : ''}`} style={{ animationDelay: `${i * 0.1}s`, opacity: triggered ? 1 : 0 }}> 
                <CompanyCard company={company} index={i} activeSlug={activeSlug} /> 
              </div> 
            ))} 
          </div> 
        </div> 
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6"> 
          {visibleAssociateCompanies.map((company, i) => ( <CompanyCard key={company.slug} company={company} index={i} activeSlug={null} /> ))} 
        </div> 
      </div> 
    </section> 
  ); 
} 

function CompanyInfoSection() { 
  const { i18n } = useTranslation(); 
  const isJa = i18n.language === 'ja'; 

  const holding = groupCompanies.find(c => c.slug === 'swell-holdings') || groupCompanies[0];
  const { info } = holding;

  const infoRows = [ 
    { label: "COMPANY NAME", value: isJa ? "株式会社スウェル・ホールディングス" : "SWELL HOLDINGS CO., LTD." }, 
    { label: "REPRESENTATIVE", value: (isJa ? info.representative_ja : info.representative).toUpperCase() }, 
    { label: "ESTABLISHED", value: info.established }, 
    { label: "CAPITAL", value: (isJa ? (info.capital_ja || info.capital) : info.capital).toUpperCase() }, 
    { label: "BUSINESS", value: (isJa ? info.business_ja : info.business).toUpperCase() }, 
    { label: "MAIN BANK", value: (isJa ? info.mainBank_ja : info.mainBank).toUpperCase() }, 
    { label: "ADDRESS", value: (isJa ? info.location_ja : info.location).toUpperCase() }, 
  ].filter(row => row.value); 

  return ( 
    <section id="company-information" className="bg-white border-t border-gray-100 py-14 md:py-24"> 
      <div className="site-container"> 
        <div className="mb-10 md:mb-14"> 
          <SectionLabel>OVERVIEW</SectionLabel> 
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>Company Information</h2> 
        </div> 
        <div className="flex flex-col lg:flex-row gap-12 items-start"> 
          <div className="flex-1 max-w-2xl w-full"> 
            {infoRows.map((row) => ( 
              <div key={row.label} className="flex flex-col sm:flex-row border-b border-gray-100 py-5"> 
                <div 
                  className="sm:w-48 shrink-0 text-[12px] tracking-[0.08em] text-gray-400 font-medium uppercase" 
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {row.label}
                </div> 
                <div className="text-[14px] text-gray-700 leading-relaxed fade-target" style={isJa ? { fontFamily: "'Noto Sans JP', sans-serif" } : {}}>{trimPunctuation(row.value || '')}</div> 
              </div> 
            ))} 
          </div> 
          <div className="flex-1 w-full h-[450px] rounded-lg overflow-hidden border border-gray-100 shadow-sm"> 
            <iframe  
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3242.2359101297234!2d139.69553857687708!3d35.64655893187604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188bfbac2751d9%3A0x2e161e75a54d87e3!2sSwell%20Coffee%20Roasters!5e0!3m2!1sja!2sjp!4v1774018912407!5m2!1sja!2sjp"  
              width="100%"  
              height="100%"  
              style={{ border: 0 }}  
              allowFullScreen  
              loading="lazy"  
              referrerPolicy="no-referrer-when-downgrade" 
            ></iframe> 
          </div> 
        </div> 
      </div> 
    </section> 
  ); 
} 

export default function HomePage() { 
  return ( 
    <Layout> 
      <div className="sticky top-0 z-0"><HeroSection /></div> 
      <div className="relative z-10 bg-white shadow-[0_-20px_50px_rgba(0,0,0,0.03)]"> 
        <PhilosophySection /> 
        <CompaniesSection /> 
        <AssociatesSection /> 
        <CompanyInfoSection /> 
      </div> 
    </Layout> 
  ); 
}
